import { PolylineControl } from "@evergis/sgis/es/controls/PolylineControl";
import { LabelFeature } from "@evergis/sgis/es/features/Label";
import { DomPainter } from "@evergis/sgis/es/painters/DomPainter/DomPainter";
import { Map as SMap } from "@evergis/sgis/es/Map";
import { PolyControlParams } from "@evergis/sgis/es/controls/PolyControl";
import { sGisMouseMoveEvent } from "@evergis/sgis/es/commonEvents";

import { ServerSnappingController } from "../ServerSnappingController";
import { ServerSnappingProvider } from "../ServerSnappingProvider";

import { tooltipSymbol } from "../TooltipSymbol";

import { i18n } from "../../../i18n";

import { DEFAULT_LABEL_POSITION } from "../../utils";

import { SnappingProviderSettings } from "../../../ducks/snapping/types";

export class BasePolylineCreator extends PolylineControl {
  snappingProvider: ServerSnappingProvider;

  private snappingController: ServerSnappingController;

  private tempLabel: LabelFeature;

  private readonly painter: DomPainter;

  private noLabel?: boolean;

  constructor(map: SMap, painter: DomPainter, params: PolyControlParams & { noLabel?: boolean }) {
    super(map, params);

    this.painter = painter;
    this.noLabel = params.noLabel;

    if (!this.noLabel) {
      this.tempLabel = new LabelFeature(DEFAULT_LABEL_POSITION, {
        crs: this.map.crs,
        symbol: tooltipSymbol,
      });
    }

    this.on("pointAdd", () => {
      if (this.tempLabel) {
        this.tempLabel.content = i18n.t("geometryEditor.polygonCreatorHint", { ns: "common" });
      }

      if (this.activeFeature && !this.activeFeature.persistOnMap) {
        this.activeFeature.persistOnMap = true;
      }
    });

    if (params?.snappingProvider) {
      this.snappingController = new ServerSnappingController({
        map,
        control: this,
        tempLabel: this.tempLabel,
        snappingProvider: this.snappingProvider,
        snappingFeature: this._getSnappingFeature(null),
      });
    }
  }

  updateSnappingProvider(snappingProviderSettings: Partial<SnappingProviderSettings>) {
    this.snappingController.updateSnappingProvider(snappingProviderSettings);
  }

  protected _handleMousemove(event: sGisMouseMoveEvent) {
    super._handleMousemove(event);
    this.drawPointOnMouseMove(event);
  }

  protected _activate() {
    super._activate();

    if (this.tempLabel) {
      this.tempLabel.content = i18n.t("clickToDrawLine", { ns: "layerList" });
      this._tempLayer.add(this.tempLabel);
    }

    if (!this.activeLayer && this.snappingController) {
      this.snappingController.setSnappingLayer(this._tempLayer);
    }

    this.snappingController && this.snappingController.activate();
    this.setCursor("pointer");
  }

  protected _deactivate() {
    super._deactivate();

    if (!this.noLabel) {
      if (this._tempLayer.has(this.tempLabel)) {
        this._tempLayer.remove(this.tempLabel);
      }

      delete this.tempLabel;
    }

    this.snappingController && this.snappingController.deactivate();
    this.setCursor("default");
  }

  private drawPointOnMouseMove(event: sGisMouseMoveEvent) {
    if (this.tempLabel) {
      this.tempLabel.content = this.activeFeature
        ? i18n.t("geometryEditor.polygonCreatorHint", { ns: "common" })
        : i18n.t("clickToDrawLine", { ns: "layerList" });

      this.tempLabel.position = event.point?.position;
    }
  }

  private setCursor(cursor: "pointer" | "default") {
    const { wrapper } = this.painter;

    if (wrapper && typeof wrapper !== "string") {
      wrapper.style.cursor = cursor;
    }
  }
}
